import React from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

// Redux
import { PollOptionNormalized, PollQuestionType } from 'redux/schemas/models/poll';

import CustomRadioGroup from 'components/custom-radio-group';
import { black, gray3, gray4, gray5, gray7, primary } from 'styles/global_defaults/colors';
import { blue05 } from 'athena/styles/colors';
import { openSans } from 'styles/global_defaults/fonts';
import {
  doubleSpacing,
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import { ReactComponent as CheckedBox } from 'styles/icons/checked-box.svg';
import { ReactComponent as UncheckedBox } from 'styles/icons/unchecked-box.svg';
import { config } from '../../../../config/pendo.config.json';

interface PollSelectOptionProps {
  options: PollOptionNormalized[];
  setSelectedOption: Function
  selectedOption: string[];
  disabled: boolean;
  type?: PollQuestionType;
  pollId: number;
}

/* @ngInject */
export default function PollSelectOption(props: PollSelectOptionProps) {
  // radio buttons uses string values but we have number Ids, so here we convert them

  const { selectedOption, setSelectedOption, disabled, pollId } = props;

  const handleOptionSelection = (optionId: string) => {
    if (selectedOption.includes(optionId)) {
      setSelectedOption(selectedOption.filter((id) => id !== optionId));
    } else {
      setSelectedOption([...selectedOption, optionId]);
    }
  };

  const optionContainerStyle = css`
    &:hover {
      /* Change outer circle color on hover */
      input[type="radio"]:not([disabled]) {
        border-color: ${primary};
      }
    }
    .option-container {
      .input-label {
        border-radius: ${quarterSpacing}px;
        background-color: ${gray7};
        svg {
          color: ${disabled ? gray5 : gray3};
          min-width: ${largeSpacing}px;
        }
        color: ${disabled ? gray4 : black};
      }
      .input-label:hover {
        border-color: ${disabled ? gray5 : primary};
        background-color: ${disabled ? gray7 : blue05};
        svg {
          color: ${disabled ? gray5 : primary};
        }
      }
      .checked {
        box-shadow: 0 0 0 1px ${primary};
        background-color: ${blue05};
        svg {
          color: ${primary};
        }
      }
    }
    .option-container:focus-within {
      .input-label {
        box-shadow: 0 0 0 2px ${primary};
      }
    }

    input[type="radio"] {
      appearance: none; /* Remove default styling */
      opacity: 1 !important;
      margin: 0px;
      top: 50%;
      left: ${standardSpacing}px;
      transform: translateY(-50%);
      width: 20px;
      height: 20px;
      border: 2px solid ${gray3};
      border-radius: 50%;
      display: inline-block;
      background-color: white;
    }

    input[type="radio"]: disabled {
      border: 2px solid ${gray5};
      background-color: ${gray7};
    }

    /* Create inner solid circle (psuedo element) when checked */
    input[type="radio"]:checked::before {
      content: "";
      width: 10px; /* Inner circle size */
      height: 10px;
      background-color: ${primary};
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    input[type="radio"]:checked {
      border-color: ${primary};
    }

    input[type="radio"]:focus {
      outline: none;
    }
  `;

  switch (props.type) {
    case PollQuestionType.MULTIPLE_CHOICE:
      return (
        <div
          css={css`
            margin-top: ${threeQuartersSpacing}px;
          `}
        >
          <p className='text-small'>
            {t.LECTURE_PAGES.COMPONENTS.POLLS.SELECT_ONE()}
          </p>
          <CustomRadioGroup
            radios={props.options.map((option, index) => ({
              value: String(option.id),
              option, // we pass the entire option object to use it latter in renderRadioButton
              disabled: props.disabled,
            }))}
            optionContainerStyle={optionContainerStyle}
            name={`poll-${pollId}`}
            onChange={(val) => {
              setSelectedOption([val]);
            }}
            activeValue={selectedOption[0]}
            radioInputDataQa={config.pendo.polls.option}
            renderRadioButton={({ option, index }) => (
              <div
                css={optionRadioButtonStyles}
                className='option-content'
              >
                {option.optionContent}
              </div>
            )}
          />
        </div>
      );
    case PollQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER:
      return (
        <div className='mt-3' css={optionContainerStyle}>
          <p className='text-small'>{t.LECTURE_PAGES.COMPONENTS.POLLS.SELECT_ALL_THAT_APPLY()}</p>
          {props.options.map((option, index) => {
            const isChecked = selectedOption?.includes(String(option.id));
            const checkBoxProps = {
              width: largeSpacing,
              height: largeSpacing,
              color: isChecked ? primary : gray3,
            };
            return (
              <div className='option-container'>
                <label
                  className={`${isChecked ? 'checked' : ''} ${!disabled ? 'cursor-pointer' : ''} input-label d-flex align-items-center mb-2 p-3`}
                  key={option.id}
                >
                  {isChecked
                    ? <CheckedBox {...checkBoxProps} />
                    : <UncheckedBox {...checkBoxProps} />}
                  <input
                    type='checkbox'
                    checked={isChecked}
                    onChange={() => handleOptionSelection(String(option.id))}
                    tabIndex={0}
                    disabled={disabled}
                    data-qa={`${config.pendo.polls.option}${index + 1}`}
                  />
                  <p className='mb-0 ml-3 text-large-body font-weight-normal'>
                    {option.optionContent}
                  </p>
                </label>
              </div>
            );
          })}
        </div>
      );
    default:
      return null;
  }
}

const optionRadioButtonStyles = css`
  &.option-content {
    color: ${black};
    background-color: ${gray7};
  }
  font-size: 16px;
  line-height: ${largeSpacing}px;
  font-weight: normal;
  font-family: ${openSans};
  border-radius: ${quarterSpacing}px;
  padding: ${threeQuartersSpacing}px ${doubleSpacing}px ${threeQuartersSpacing}px 55px;
  margin-bottom: ${halfSpacing}px;

  input:checked + & {
    box-shadow: 0 0 0 1px ${primary};
  }
  input:focus + & {
    box-shadow: 0 0 0 2px ${primary};
  }

  input:not([disabled]) + &:hover,
  input:checked + & {
    background-color: ${blue05} !important;
  }
  input:disabled + & {
    color: ${gray4};
  }
`;
